import type { Audit, AuditStatus, AuditSubjectKind } from '@l2u/web-shared';

// Search + To do/Completed narrowing for the audits list (MyAudits.tsx). Pure so
// the matching rules are testable without a DOM, the same way dashboardStats.ts
// holds the health-panel arithmetic out of Dashboard.tsx.

/**
 * The react-query key for `/api/audits/mine`. Three readers and one writer
 * share it — MyAudits.tsx, Dashboard.tsx's mobile feed, and the invalidation
 * AuditsSection fires after assigning — so it is a constant, not a literal:
 * an invalidation against a misspelt key silently does nothing.
 */
export const MY_AUDITS_QUERY_KEY = ['my-audits'] as const;

/**
 * The page's "Include deactivated" variant gets its own key (the API only
 * returns a deactivated owner's audits with `include_deactivated=1`), so it
 * never lands in the default entry the badge and dashboard feed read. It is a
 * child of the shared key, so invalidating MY_AUDITS_QUERY_KEY refreshes both.
 */
export function myAuditsQueryKey(includeDeactivated: boolean) {
  return includeDeactivated
    ? ([...MY_AUDITS_QUERY_KEY, 'with-deactivated'] as const)
    : MY_AUDITS_QUERY_KEY;
}

type StatusEmbed = { status?: string } | null | undefined;
type OwnedStatusEmbed = { status?: string; company?: StatusEmbed } | null | undefined;

/**
 * True when the audit's subject or its owning company is deactivated — a
 * driver or asset can be reactivated on its own while its company stays down,
 * so both levels are read. The client twin of the API's auditOwnerDeactivated
 * (lib/deactivation.ts); such a row only reaches the page with "Include
 * deactivated" ticked, and is tagged. Takes the embeds alone so a corrective
 * action's narrower audit embed fits too. Asset 'inactive' (out of service) is
 * not deactivation.
 */
export function auditOwnerDeactivated(audit: {
  company?: StatusEmbed;
  asset?: OwnedStatusEmbed;
  driver?: OwnedStatusEmbed;
}): boolean {
  return [
    audit.company,
    audit.asset,
    audit.asset?.company,
    audit.driver,
    audit.driver?.company,
  ].some((r) => r?.status === 'deactivated');
}

/** The two halves of the To do / Completed segmented filter. */
export type AuditFilterKey = 'todo' | 'done';

// 'assigned' and 'in_progress' are both still-open states; a finalised audit —
// every status that is not open (compliant / not_compliant / corrected) — is
// read-only. auditStatusMeta.ts's isAuditFinalised builds on this rule, and the
// runner reads that to decide whether it still accepts answers, so a row's CTA
// and the runner it opens always agree.
export function isOpenAudit(status: AuditStatus): boolean {
  return status === 'assigned' || status === 'in_progress';
}

/**
 * The owning company to show as row CONTEXT, or null when there is none worth
 * showing (RULING R73).
 *
 * A row's title already names the audit's subject (`auditSubjectLabel`), and a
 * meta line's job is context *beyond* the title. For a COMPANY audit the owner
 * and the subject are the same record, so printing the company name there
 * repeats the title verbatim and adds nothing — the card showed "Acme Freight"
 * twice. For an asset or driver audit the owner is a different record and is
 * real context, which is why this is a null rather than a rename.
 *
 * Shared by all three rows that name an audit — MyAudits.tsx's card,
 * Dashboard.tsx's today's-audits row and CorrectiveActions.tsx's item — so they
 * cannot disagree about it. They still COMPOSE it differently on purpose,
 * because their rows differ: the card renders the questionnaire name on its own
 * line and simply drops this segment when it is null, the dashboard row has no
 * such line and falls back to the questionnaire name in its place, and the
 * corrective-action row drops it and shows the subject alone.
 *
 * The parameter is the two fields this actually reads rather than a whole
 * `Audit`, the same way web-shared's `auditSubjectLabel` takes
 * `AuditSubjectEmbeds`: a corrective action carries its audit as a narrower
 * embed, and widening the callers to fake a full `Audit` would be worse than
 * narrowing the parameter to the truth.
 */
export type AuditOwnerSubject = {
  subject_kind: AuditSubjectKind;
  company?: { company_name: string } | null;
};

export function auditOwnerContext(audit: AuditOwnerSubject): string | null {
  if (audit.subject_kind === 'company') return null;
  return audit.company?.company_name ?? null;
}

// Case-folded, whitespace-collapsed form used on both sides of the comparison.
// The design prototype only normalised the haystack, which meant a query typed
// with a stray double space matched nothing; normalising both is a superset of
// its behaviour rather than a different rule.
function normalise(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Everything the search box matches on.
 *
 * THE INVARIANT: anything visible on a row can be typed to find it. That is the
 * rule, not the list below — the list is only today's spelling of it. An audit's
 * subject is one of three records (migration 0048), so the haystack has to cover
 * all three: an ASSET by plate and year/make/model, a DRIVER by name, a COMPANY
 * by company name. If you add a field to the card in MyAudits.tsx, add it here
 * too, or you have made a row the search box cannot find. That is exactly how
 * this function came to drop every driver and company audit — the card grew two
 * new subjects and the haystack did not follow.
 *
 * The fields are collected from every embed present rather than switched on
 * `subject_kind`, so a row can never become unfindable because its
 * `subject_kind` and its embeds disagree. `company` is deliberately included for
 * an asset or driver audit too: there it is the OWNER, and the card already
 * renders it on the meta line.
 */
export function auditSearchText(audit: Audit): string {
  const asset = audit.asset;
  return normalise(
    [
      asset?.registration_plate,
      asset?.year,
      asset?.make,
      asset?.model,
      audit.driver?.name,
      audit.company?.company_name,
    ]
      .filter(Boolean)
      .join(' '),
  );
}

/**
 * Rows for the selected segment, narrowed by the search box. An empty query
 * matches everything, so the segment alone drives the list until the auditor
 * starts typing.
 */
export function filterAudits(audits: Audit[], filter: AuditFilterKey, query: string): Audit[] {
  const needle = normalise(query);
  const wantOpen = filter === 'todo';
  return audits.filter(
    (audit) =>
      isOpenAudit(audit.status) === wantOpen &&
      (needle === '' || auditSearchText(audit).includes(needle)),
  );
}
